import { createPortal } from 'react-dom'

/**
 * ProjectBriefPrintable — the Project Brief as a document.
 *
 * Hidden on screen; the print stylesheet (styles/print.css) hides the
 * application chrome and lays this node out on A4, so Ctrl/⌘-P produces the
 * brief rather than a screenshot of the app.
 *
 * It is portalled to <body> rather than left inside the workspace tree. The
 * workspace renders it inside <main>, which is a positioned, padded flex
 * column: a document anchored there inherits that column's offset and width,
 * and — because the rest of the app keeps its height on paper — prints trailing
 * blank pages and clips a brief longer than the page it sits on. As a direct
 * child of <body> the document owns the printed page: full width, correct
 * origin, and as many pages as its own content needs.
 *
 * It renders the same model the PDF exporter draws — no second copy of the
 * project content, and no data of its own.
 */
export default function ProjectBriefPrintable({ model }) {
  if (!model || typeof document === 'undefined') return null

  const doc = (
    <article className="project-brief-print" aria-hidden="true">
      <header className="pb-head">
        <span className="pb-eyebrow">SMA Project Lab · Project Brief</span>
        <h1 className="pb-title">{model.title}</h1>
        {model.summary && <p className="pb-summary">{model.summary}</p>}

        {model.meta.length > 0 && (
          <dl className="pb-meta">
            {model.meta.map((m) => (
              <div key={m.label} className="pb-meta-item">
                <dt>{m.label}</dt>
                <dd>{m.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {model.finalDeliverable && (
          <div className="pb-deliverable">
            <span className="pb-eyebrow">Final deliverable</span>
            <p>{model.finalDeliverable}</p>
          </div>
        )}
      </header>

      {model.sections.length > 0 && (
        <section className="pb-section-group">
          <h2 className="pb-h2">Project Brief</h2>
          {model.sections.map((section, i) => (
            <section key={`${section.title}-${i}`} className="pb-block">
              {section.title && <h3 className="pb-h3">{section.title}</h3>}
              {section.content && <p className="pb-body">{section.content}</p>}
            </section>
          ))}
        </section>
      )}

      {model.stages.length > 0 && (
        <section className="pb-section-group">
          <h2 className="pb-h2">Production Stages</h2>
          {model.stages.map((stage) => (
            <section key={stage.number} className="pb-stage">
              <h3 className="pb-h3">
                <span className="pb-stage-number">{String(stage.number).padStart(2, '0')}</span>
                {stage.title}
              </h3>
              <p className="pb-mode">{stage.modeLabel}</p>
              {stage.objective && <p className="pb-body">{stage.objective}</p>}
              {stage.description && stage.description !== stage.objective && (
                <p className="pb-body pb-muted">{stage.description}</p>
              )}

              {stage.guidance.length > 0 && (
                <div className="pb-list-group">
                  <span className="pb-eyebrow">Guidance</span>
                  <ul className="pb-list">
                    {stage.guidance.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              )}
              {stage.requirements.length > 0 && (
                <div className="pb-list-group">
                  <span className="pb-eyebrow">Requirements</span>
                  <ul className="pb-list">
                    {stage.requirements.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              )}
              {stage.acceptance.length > 0 && (
                <div className="pb-list-group">
                  <span className="pb-eyebrow">Acceptance criteria</span>
                  <ul className="pb-list">
                    {stage.acceptance.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              )}
              {stage.submission && (
                <p className="pb-submission">
                  Files required for submission
                  {stage.submission.maxFiles ? ` · up to ${stage.submission.maxFiles} files` : ''}
                  {stage.submission.acceptedTypes ? ` · accepted: ${stage.submission.acceptedTypes}` : ''}
                  {stage.submission.notesRequired ? ' · submission notes required' : ''}
                </p>
              )}
            </section>
          ))}
        </section>
      )}

      <footer className="pb-foot">Synergy Multimedia Academy · Project Lab</footer>
    </article>
  )

  return createPortal(doc, document.body)
}
